/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from "react";

/** Common Components */
import FormCreator from "../../../../../../../components/finalForms/FormCreator";
import Buttons from "../../../../../../../components/ui/button/Buttons";
import MolGrid, { Column } from "@moltech/molui/molgrid";

/** Configuration files */
import { InputText } from "primereact/inputtext";
import { newScopesConfig } from "./NewContractScope.formfield.config";
import { useGetClientByContractIdMutation, useInsertNewScopesWithCheckSerialNumberMutation, useLazyGetContractDepartmentsQuery, useLazyGetInstrumentTypesForContractQuery } from "../../../../../../../app/services/contractAPI";
import { useLazyGetInstrumentTypeListQuery, useLazyGetScopeTypeListQuery } from "../../../../../../../app/services/commonAPI";
import { mapDropdownOptions } from "../../../../../../../components/finalForms/libs/data/mapDropdownOptions";
import { ContractContext } from "../../../../../../../utils/context/ContractContext";
import ToastService from "../../../../../../../services/toastService/ToastService";
import SwalAlert from "../../../../../../../services/swalService/SwalService";

const NewScopes = ({ onClose }) => {

  const { confirm, info } = SwalAlert();
  const { contractData, contractId } = useContext(ContractContext);
  const NewScopesRef = useRef();
  const [contractScopeForm, setContractScopeForm] = useState(newScopesConfig);
  const [count, setCount] = useState();

  const [gridData, setGridData] = useState(() =>
    Array.from({ length: count }, () => ({
      serialNumber: "",
      cost: "$0.00",
    }))
  );

  const [getAllClientsByContractId, {
    isFetching: isAllClientsByContractIdFetching,
    isSuccess: isAllClientsByContractIdFetched,
    data: allClientsByContractIdData,
  },] = useGetClientByContractIdMutation();

  const [getContractDepartments, {
    isFetching: isContractDepartmentsFetching,
    isSuccess: isContractDepartmentsFetched,
    data: ContractDepartmentsData,
  },] = useLazyGetContractDepartmentsQuery();

  const [getInstrumentTypes, {
    isFetching: isInstrumentFetching,
    isSuccess: isInstrumentSuccess,
    data: instrumentTypeData
  }] = useLazyGetInstrumentTypeListQuery();

  const [getAllScopeTypes, {
    isFetching: isScopeTypeFetching,
    isSuccess: isScopeTypeFetched,
    data: scopeTypesData
  }] = useLazyGetScopeTypeListQuery();

  const [insertNewScopesWithCheckSerialNumber] = useInsertNewScopesWithCheckSerialNumberMutation();

  useEffect(() => {
    if (contractId && contractData) {
      getAllClientsByContractId(contractId);
      getInstrumentTypes();
      let formData = { ...newScopesConfig }
      formData.initialState = {
        pdtEffectiveDate: new Date(contractData?.dtDateEffective),
        pdtEndDate: new Date(contractData?.dtDateTermination)
      }
      setContractScopeForm(formData);
    }
  }, [contractId, contractData]);

  useEffect(() => {
    if (count > 0) {
      const rows = Array.from({ length: count }, () => ({
        serialNumber: "",
        cost: "$0.00",
      }));
      setGridData(rows);
    }
  }, [count]);

  useEffect(() => {
    if (!isAllClientsByContractIdFetching && isAllClientsByContractIdFetched && allClientsByContractIdData) {
      const updatedConfig = { ...contractScopeForm };
      mapDropdownOptions(updatedConfig, "NewScopeSection", "lClientKey", allClientsByContractIdData, "lClientKey", "sClientName1");
      setContractScopeForm(updatedConfig);
    }
  }, [isAllClientsByContractIdFetching, isAllClientsByContractIdFetched, allClientsByContractIdData]);

  useEffect(() => {
    if (!isContractDepartmentsFetching && isContractDepartmentsFetched && ContractDepartmentsData) {
      const updatedConfig = { ...contractScopeForm };
      mapDropdownOptions(updatedConfig, "NewScopeSection", "lDepartmentKey", ContractDepartmentsData, "lDepartmentKey", "department");
      setContractScopeForm(updatedConfig);
    }
  }, [isContractDepartmentsFetching, isContractDepartmentsFetched, ContractDepartmentsData]);

  useEffect(() => {
    if (!isInstrumentFetching && isInstrumentSuccess && instrumentTypeData) {
      const updatedConfig = { ...contractScopeForm };
      mapDropdownOptions(updatedConfig, 'NewScopeSection', 'sInstrumentTypeKey', instrumentTypeData, 'sInstrumentTypeKey', 'sInstrumentType')
      setContractScopeForm(updatedConfig);
    }
  }, [isInstrumentFetching, isInstrumentSuccess, instrumentTypeData])

  useEffect(() => {
    if (!isScopeTypeFetching && isScopeTypeFetched && scopeTypesData) {
      const updatedConfig = { ...contractScopeForm };
      mapDropdownOptions(updatedConfig, 'NewScopeSection', 'plScopeTypeKey', scopeTypesData, 'lScopeTypeKey', 'sScopeTypeDesc')
      setContractScopeForm(updatedConfig);
    }
  }, [isScopeTypeFetching, isScopeTypeFetched, scopeTypesData]);

  const handleFieldChange = (dataField, updatedFormData) => {
    let updatedState = { ...updatedFormData };
    if (dataField === "lClientKey") {
      const selectedClient =
        updatedFormData.lClientKey?.value ?? updatedFormData.lClientKey;
      getContractDepartments({
        plContractDepartmentKey: 0,
        plClientKey: selectedClient,
        plContractKey: contractId,
      });
      updatedState = {
        ...updatedState,
        lDepartmentKey: null
      };
      setContractScopeForm((prev) => ({
        ...prev,
        initialState: updatedState,
      }));
      return;
    }

    if (dataField === "sInstrumentTypeKey") {
      const selectedInstrumentType =
        updatedFormData.sInstrumentTypeKey?.value ??
        updatedFormData.sInstrumentTypeKey;
      getAllScopeTypes(selectedInstrumentType);
      updatedState = {
        ...updatedState,
        plScopeTypeKey: null,
        lModelKey: null,
      };
      setContractScopeForm((prev) => ({
        ...prev,
        initialState: updatedState,
      }));
      return;
    }

    if (dataField === "pdtEffectiveDate") {
      if (updatedFormData?.pdtEffectiveDate < new Date(contractData?.dtDateEffective)) {
        info("Updating Effective Date", "The effective date cannot be prior to the effective date of the contract.");
        updatedState = {
          ...updatedState,
          pdtEffectiveDate: new Date(contractData?.dtDateEffective),
        };
      }
    }

    if (dataField === "pdtEndDate") {
      if (updatedFormData?.pdtEndDate > new Date(contractData?.dtDateTermination)) {
        info("Updating Expiration Date", "The expiration date cannot be after the termination date of the contract.");
        updatedState = {
          ...updatedState,
          pdtEndDate: new Date(contractData?.dtDateTermination),
        };
      }
    }
    setContractScopeForm((prev) => ({
      ...prev,
      initialState: updatedState,
    }));
  };

  const textEditor = (options) => {
    return (
      <InputText
        value={options.value || ""}
        onChange={(e) => options.editorCallback(e.target.value)}
        style={{ width: "70%" }}
        onFocus={(e) => {
          e.target.select();
        }}
      />
    );
  };

  const onCellEditComplete = (e) => {
    const { rowIndex, field, newValue } = e;
    setGridData((prev) => {
      const updated = [...prev];
      let finalValue = newValue;
      if (field === "cost") {
        const num = parseFloat(newValue) || 0;
        finalValue = `$${num.toFixed(2)}`;
      }
      updated[rowIndex] = { ...updated[rowIndex], [field]: finalValue };
      return updated;
    });
  };

  const onFieldBlure = (dataField, updatedData) => {
    if (dataField === "count") {
      const newCount = Number(updatedData?.count || 0);
      setCount(newCount);
    }
  };

  const handleSave = async () => {
    let formData = NewScopesRef.current.getFormData();
    if (formData) {
      if (formData?.pdtEffectiveDate > formData?.pdtEndDate) {
        ToastService.error("Expiration Date cannot be prior to Effective Date");
        return
      }
      const invalidSerials = gridData.filter(x => !x.serialNumber || x.serialNumber.trim().length === 0);

      if (invalidSerials.length > 0) {
        if (gridData.length === 1) {
          ToastService.error("Invalid Serial Number.");
        } else {
          ToastService.error("There is at least one invalid Serial Number.");
        }
        return;
      }
      const req = gridData.map(i => ({
        psSerialNumber: i.serialNumber,
        pnCost: parseFloat((i.cost + "").replace(/[^0-9.]/g, "")) || 0,
        plScopeTypeKey: formData?.plScopeTypeKey?.value || 0,
        pdtEffectiveDate: formData?.pdtEffectiveDate,
        pdtEndDate: formData?.pdtEndDate,
        plContractKey: contractId,
        plDepartmentKey: formData?.lDepartmentKey?.value,
        pbRenewal: false,
      }));
      const result = await insertNewScopesWithCheckSerialNumber(req).unwrap();
      const existingSerials = result.filter(r => r.exists === true);
      if (existingSerials.length === 0) {
        ToastService.success("Scopes inserted successfully");
        onClose();
        resetForm();
        return;
      }
      confirm(
        `Serial numbers already exist:\n${existingSerials.map(x => x.serialNumber).join(", ")}\n\nDo you want to insert them anyway?`,
        "",
        "Yes",
        "No",
      ).then(async (confirmed) => {
        if (!confirmed) {
          ToastService.info("Process stopped by user.");
          onClose();
          resetForm();
          return;
        }
        const retryPayload = existingSerials.map(x => {
          const original = req.find(p => p.psSerialNumber === x.serialNumber);
          return {
            ...original,
            forceInsert: true
          };
        });
        await insertNewScopesWithCheckSerialNumber(retryPayload);
        onClose();
        resetForm();
        ToastService.success("All duplicate scopes inserted successfully");
      });
    };
  }

  const resetForm = () => {
    const updatedForm = { ...contractScopeForm };
    updatedForm.initialState = {};
    setContractScopeForm(updatedForm);
  };


  return (
    <div className="row">
      <FormCreator
        ref={NewScopesRef}
        config={contractScopeForm}
        onColumnChange={handleFieldChange}
        onFieldBlure={onFieldBlure}
      />
      <div className="scrollable-grid col-12">
        <MolGrid
          value={gridData}
          grouping
          expandableRowGroups={true}
          paginator={false}
          scrollable
          scrollHeight="flex"
          filterDisplay="row"
          sortMode="single"
          showGridlines
          resizableColumns
          metaKeySelection={true}
          editMode="cell"
        >
          <Column
            field="serialNumber"
            header="Serial Number"
            editor={textEditor}
            onCellEditComplete={onCellEditComplete}
            style={{ width: "60%" }}
          />
          <Column
            field="cost"
            header="Cost"
            editor={textEditor}
            onCellEditComplete={onCellEditComplete}
            style={{ width: "20%" }}
          />
        </MolGrid>
      </div>

      <div className="d-flex justify-content-end gap-3 mt-3">
        <Buttons
          buttonText="Save"
          buttonTypeClassName="btn blue-btn"
          titleText="Save new scope"
          onClick={handleSave}
        />
        <Buttons
          buttonText="Cancel"
          onClick={onClose}
          buttonTypeClassName="btn dark-btn"
          titleText="Cancel and close"
        />
      </div>
    </div>
  );
};

export default NewScopes;
